import { usePropertyStore } from '@/common/stores/property';
import {
  deleteCookies,
  getCookies,
  setCookies,
  encrypt,
  webStorageController,
} from '@/common/utils/browser';
import { computed, ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import axios from 'axios';
import {
  postV6Login,
  postV6RandomRecreation,
  postV6TwoFactorAuthentication,
} from '@/api-client/userAndPermission/queries/user-v6-controller/user-v6-controller';
import { checkValid } from '@/common/utils/validation';
import {
  postV7HyundaiSsoOtp,
  postV7HyundaiSsoFidoRes,
} from '@/api-client/sso/queries/hyundai-sso-controller/hyundai-sso-controller';
import router from '@/common/router';
import { REG_EXP, STORAGE_KEY } from '@/common/utils/define';
import { setHeaderConfig } from '@/worker/commands/config/apiInstance';
import { getEncryptString, getPublicRsaKey } from '@/common/utils/rsa';
import { useApiRsa } from '@/common/utils/rsa/apiRsa';
import { getV6OtpGenerate } from '@/api-client/metav6/queries/google-otp-controller/google-otp-controller';
import { i18n } from '@/common/locale';
import { useMessageBox } from '@/common/inject/useMessageBox';
import {
  postApiV7KeepLogged,
  postApiV8TokenExchange,
  postApiV1RefreshToken,
} from '@/api-client/gateway/queries/default/default';
import { postApiV6Logout } from '@/api-client/gateway/queries/authentication/authentication';
import { clearPrefetchedCache } from '@/common/lib/prefetch';
import { useLinkManagerStore } from './link-manager';
import { getTzTimeNow } from '../utils/time.utils';
import { useEventListener } from '@vueuse/core';

type TokenType = 'refreshToken' | 'accessToken';
type OptionalTokenType = 'sessionToken';
type Token = Record<TokenType, string> & Partial<Record<OptionalTokenType, string>>;
type TokenInfoInCookies = {
  sessionToken: string;
};

interface LoginResultInfo {
  resultCode: string;
  resultText: string;
}

interface ExpireTimeInfo {
  timer: ReturnType<typeof setTimeout> | null;
  isPending: boolean;
}

interface PasswordInfo {
  isChange: boolean;
  isExpired: boolean;
  expireDay: number;
}

type ReturnTypeContainsToken<
  T extends (...args: any) => any,
  K extends keyof Awaited<ReturnType<T>>,
> = Awaited<ReturnType<T>>[K] & Token;

export type LoginData = ReturnTypeContainsToken<typeof postV6Login, 'data'> & {
  isPasswordExpired: boolean;
  passwordExpireDay: number;
  twoFactorType: string;
  isSecret: boolean;
};

const TOKEN_CHECK_CYCLE_SEC = 10 * 1_000;
const ACCESS_TOKEN_EXPIRATION_MIN = 15; // Access Token 유효 기간 (15분)

const getAccessTokenExpiredTime = () => {
  return +getTzTimeNow().add(ACCESS_TOKEN_EXPIRATION_MIN - 1, 'minute');
};

const setTokenInHeader = (accessToken: string) => {
  const tokenConfig = { key: 'Authorization', value: `Bearer ${accessToken}` };
  setHeaderConfig([tokenConfig]);
};

const initTokenInHeader = () => {
  const tokenConfig = { key: 'Authorization', value: null };
  setHeaderConfig([tokenConfig]);
};

const useToken = () => {
  const tokenInfo = ref<Token>({
    refreshToken: '',
    accessToken: '',
    sessionToken: '',
  });
  const accessTokenExpiredTime = ref<number>(-1);

  const setTokenInfo = ({ accessToken, refreshToken, sessionToken }: Token) => {
    tokenInfo.value.accessToken = accessToken;
    tokenInfo.value.refreshToken = refreshToken;
    tokenInfo.value.sessionToken = sessionToken;
  };

  const setAccessTokenExpiredTime = () => {
    accessTokenExpiredTime.value = getAccessTokenExpiredTime();
  };

  return {
    tokenInfo,
    accessTokenExpiredTime,
    setTokenInfo,
    setAccessTokenExpiredTime,
  };
};

const useTokenInCookies = () => {
  const getTokenInCookies = () => {
    return getCookies(STORAGE_KEY.TOKEN);
  };

  const setTokenInCookies = (sessionToken: string) => {
    setCookies(STORAGE_KEY.TOKEN, encrypt.encode(JSON.stringify({ sessionToken })), {
      path: '/',
      sameSite: 'strict',
    });
  };

  const hasTokenInCookies = () => {
    return !!getTokenInCookies();
  };

  const removeTokenInCookies = () => {
    deleteCookies(STORAGE_KEY.TOKEN);
  };

  const checkTokenInCookies = (keepLoggedIn: boolean, hasAccessToken: boolean) => {
    if (keepLoggedIn && hasAccessToken && !getCookies(STORAGE_KEY.TOKEN)) {
      return false;
    }
    return true;
  };
  return {
    hasTokenInCookies,
    getTokenInCookies,
    setTokenInCookies,
    removeTokenInCookies,
    checkTokenInCookies,
  };
};

const usePasswordValidation = () => {
  const { confirmMsg } = useMessageBox();

  const passwordInfo = ref<PasswordInfo>({
    isChange: false,
    isExpired: false,
    expireDay: 0,
  });

  const setPasswordInfo = ({ isChange, isExpired, expireDay }: PasswordInfo) => {
    passwordInfo.value = {
      isChange,
      isExpired,
      expireDay,
    };

    webStorageController.setItem({
      type: 'session',
      key: STORAGE_KEY.PASSWORD_VALIDATION,
      value: JSON.stringify(passwordInfo.value),
    });
  };

  const isValidPasswordByStorage = () => {
    const { isChange = false, isExpired = false } = JSON.parse(
      webStorageController.getItem({
        type: 'session',
        key: STORAGE_KEY.PASSWORD_VALIDATION,
      }) ?? '{}',
    );

    webStorageController.removeItem({
      type: 'session',
      key: STORAGE_KEY.PASSWORD_VALIDATION,
    });

    return isChange || isExpired;
  };

  const showPasswordChangeConfirmation = ({ okCallback }: { okCallback: () => void }): boolean => {
    const { isChange, isExpired, expireDay } = passwordInfo.value;
    if (isChange) {
      // 최초 로그인
      confirmMsg({
        msgStr: `${i18n.global.t('MESSAGE.TEMP_PASSWORD_LOGIN')} ${i18n.global.t(
          'MESSAGE.TO_CHANGE_PASSWORD',
        )}`,
        okCallback,
        showCancelBtn: false,
      });
      return true;
    }
    if (isExpired) {
      confirmMsg({
        msgStr: `${i18n.global.t('MESSAGE.PASSWORD_NOT_CHANGED', {
          value: expireDay,
        })} ${i18n.global.t('MESSAGE.CHANGE_PASSWORD')}`,
        okCallback,
        showCancelBtn: false,
      });
      return true;
    }
    return false;
  };

  return {
    passwordInfo,
    setPasswordInfo,
    isValidPasswordByStorage,
    showPasswordChangeConfirmation,
  };
};

export const useAuthStore = defineStore('authStore', () => {
  const isVisible = ref<boolean>(true);
  const expiredTimeInfo = ref<ExpireTimeInfo>({
    timer: null,
    isPending: false,
  });
  const loginResult = ref<LoginResultInfo>({
    resultCode: '',
    resultText: '',
  });

  const isCallable = computed<boolean>(() => isVisible.value && !expiredTimeInfo.value.isPending);

  const { propertyData } = storeToRefs(usePropertyStore());

  const { tokenInfo, accessTokenExpiredTime, setTokenInfo, setAccessTokenExpiredTime } = useToken();

  const {
    hasTokenInCookies,
    getTokenInCookies,
    setTokenInCookies,
    removeTokenInCookies,
    checkTokenInCookies,
  } = useTokenInCookies();

  const {
    passwordInfo,
    setPasswordInfo,
    isValidPasswordByStorage,
    showPasswordChangeConfirmation,
  } = usePasswordValidation();

  const linkManagerStore = useLinkManagerStore();
  const { resetLinkManagerData, resetLinkToken } = linkManagerStore;
  const { linkToken } = storeToRefs(linkManagerStore);

  const initAuth = () => {
    tokenInfo.value = {
      refreshToken: '',
      accessToken: '',
      sessionToken: '',
    };
    setPasswordInfo({
      isChange: false,
      isExpired: false,
      expireDay: 0,
    });
    initTokenInHeader();
  };

  const setStorage = () => {
    const { accessToken, refreshToken, sessionToken } = tokenInfo.value;

    webStorageController.setItem({
      type: 'session',
      key: STORAGE_KEY.TOKEN,
      value: encrypt.encode(
        JSON.stringify({
          accessToken,
          refreshToken,
          sessionToken,
          expiredTime: accessTokenExpiredTime.value,
        }),
      ),
    });
  };

  const clear = () => {
    initAuth();
    webStorageController.clear('session');
    removeTokenInCookies();
    clearPrefetchedCache();
  };

  const setTokenLogout = async () => {
    if (!tokenInfo.value.refreshToken) {
      return;
    }

    try {
      await postApiV6Logout({
        refreshToken: tokenInfo.value.refreshToken,
      });
    } catch (e) {
      console.log(e);
    }
  };

  const updateLoginInfo = (loginData: LoginData) => {
    const {
      accessToken,
      refreshToken,
      sessionToken = '',
      passwordChange,
      isPasswordExpired: pwExpired,
      passwordExpireDay: pwExpireDay,
    } = loginData;
    setTokenInfo({ accessToken, refreshToken, sessionToken });
    setTokenInHeader(accessToken);

    if (passwordChange === false && pwExpired === false) {
      setAccessTokenExpiredTime();
      setStorage();
    }

    /**
     * keepLoggedIn: true
     * 쿠키에 sessionToken 정보를 저장함
     */
    if (propertyData.value?.keepLoggedIn && !hasTokenInCookies() && tokenInfo.value.sessionToken) {
      setTokenInCookies(tokenInfo.value.sessionToken);
    }

    setPasswordInfo({
      isChange: !!passwordChange,
      isExpired: !!pwExpired,
      expireDay: pwExpireDay,
    });
  };

  const login = async (loginData: {
    id: string;
    password: string;
  }): Promise<{
    twoFactorType: string;
    isRegistered: boolean;
    userEmail: string;
  } | null> => {
    const { rsaKey: publicRsaKey, rsaId } = await getPublicRsaKey();
    const loginDataIdKey = checkValid(REG_EXP.EMAIL, loginData.id) ? 'email' : 'activeId';
    const encryptedLoginData = {
      [loginDataIdKey]: loginData.id,
      password: getEncryptString(loginData.password, publicRsaKey) as string,
    };

    const { data } = await useApiRsa().handleApiClientRsaId({
      fn: postV6Login,
      params: encryptedLoginData,
      rsaId,
    });

    // TODO api 응답 모델하고 전혀 다른 데이터가 응답되고 있음, BE팀 사정으로 model update 보류 요청, 임시로 강제 형변환으로 사용
    const loginResponse = data as LoginData;
    if (!loginResponse) {
      return null;
    }
    updateLoginInfo(loginResponse);

    const {
      twoFactorAuthentication, // 2차인증 사용여부
      twoFactorType,
      isSecret, // 2차인증 등록여부
    } = loginResponse;

    if (!twoFactorAuthentication || !twoFactorType) {
      return null;
    }

    return { twoFactorType, isRegistered: !!isSecret, userEmail: loginData.id };
  };

  const ssoLogin = async (id?: string) => {
    const response = await axios.get(`/api/v6/sso/${id}`, {
      responseType: 'json',
    });
    const loginResponse = response.data;

    if (!loginResponse) {
      return;
    }

    const { accessToken = '', refreshToken = '', sessionToken = '' } = loginResponse;

    setTokenInfo({ accessToken, refreshToken, sessionToken });
    setAccessTokenExpiredTime();
    setTokenInHeader(accessToken);
    setStorage();

    /**
     * keepLoggedIn: true
     * 쿠키에 sessionToken 정보를 저장함
     */
    if (propertyData.value?.keepLoggedIn && !hasTokenInCookies() && tokenInfo.value.sessionToken) {
      setTokenInCookies(tokenInfo.value.sessionToken);
    }
  };

  const shinhanLogin = async (activeId: string, signal?: AbortSignal) => {
    const { data } = await axios.post(
      '/api/v1/shinhanbank/sso',
      { activeId },
      { responseType: 'json', signal },
    );

    if (!data) {
      return;
    }

    updateLoginInfo(data);
  };

  const otpLogin = async (loginData: { id: string; otp: string }) => {
    initTokenInHeader();

    const { rsaKey: publicRsaKey, rsaId } = await getPublicRsaKey();
    const encryptedLoginData = {
      id: loginData.id,
      otp: getEncryptString(loginData.otp, publicRsaKey) as string,
    };
    const { data } = await useApiRsa().handleApiClientRsaId({
      fn: postV7HyundaiSsoOtp,
      params: encryptedLoginData,
      rsaId,
    });
    const responseData = data as ReturnTypeContainsToken<typeof postV7HyundaiSsoOtp, 'data'>;
    const {
      accessToken = '',
      refreshToken = '',
      sessionToken = '',
      resultCode = '',
      resultText = '',
    } = responseData || {};

    setTokenInfo({ accessToken, refreshToken, sessionToken });
    setAccessTokenExpiredTime();

    loginResult.value.resultCode = resultCode;
    loginResult.value.resultText = resultText;

    setTokenInHeader(accessToken);
    setStorage();

    /**
     * keepLoggedIn: true
     * 쿠키에 sessionToken 정보를 저장함
     */
    if (propertyData.value?.keepLoggedIn && !hasTokenInCookies() && tokenInfo.value.sessionToken) {
      setTokenInCookies(tokenInfo.value.sessionToken);
    }
  };

  const fidoLogin = async (loginData: { uid: string; fid: string }) => {
    initTokenInHeader();
    const { rsaKey: publicRsaKey, rsaId } = await getPublicRsaKey();
    const { data } = await useApiRsa().handleApiClientRsaId({
      fn: postV7HyundaiSsoFidoRes,
      params: {
        uid: loginData.uid,
        fid: getEncryptString(loginData.fid ?? '', publicRsaKey) as string,
      },
      rsaId,
    });
    const responseData = data as ReturnTypeContainsToken<typeof postV7HyundaiSsoFidoRes, 'data'>;
    const {
      accessToken = '',
      refreshToken = '',
      sessionToken = '',
      resultCode = '',
      resultText = '',
    } = responseData || {};

    setTokenInfo({ accessToken, refreshToken, sessionToken });
    setAccessTokenExpiredTime();

    loginResult.value.resultCode = resultCode;
    loginResult.value.resultText = resultText;

    setTokenInHeader(accessToken);
    setStorage();

    /**
     * keepLoggedIn: true
     * 쿠키에 sessionToken 정보를 저장함
     */
    if (propertyData.value?.keepLoggedIn && !hasTokenInCookies() && tokenInfo.value.sessionToken) {
      setTokenInCookies(tokenInfo.value.sessionToken);
    }
  };

  const requestTwoFactorCode = async () => {
    return postV6RandomRecreation();
  };

  const registerNewGoogleOtp = async (
    userEmail: string,
  ): Promise<{ qrUrl: string; secretKey: string }> => {
    const {
      data: { qrUrl, secretKey },
    } = await getV6OtpGenerate({ userEmail });

    if (!qrUrl || !secretKey) {
      throw new Error();
    }

    return { qrUrl: qrUrl || '', secretKey: secretKey || '' };
  };

  const completeTwoFactor = async (twoFactorCode: string, activeId: string, rsaId?: string) => {
    try {
      const { data } = await useApiRsa().handleApiClientRsaId({
        fn: postV6TwoFactorAuthentication,
        params: { code: twoFactorCode, activeId },
        rsaId,
      });
      const twoFactorResponse = data as LoginData;
      const { accessToken, refreshToken, sessionToken } = twoFactorResponse;

      setTokenInfo({ accessToken, refreshToken, sessionToken });
      setAccessTokenExpiredTime();
      setTokenInHeader(accessToken);
      setStorage();

      /**
       * keepLoggedIn: true
       * 쿠키에 sessionToken 정보를 저장함
       */
      if (
        propertyData.value?.keepLoggedIn &&
        !hasTokenInCookies() &&
        tokenInfo.value.sessionToken
      ) {
        setTokenInCookies(tokenInfo.value.sessionToken);
      }
    } catch {
      throw new Error('Two-factor authentication failed');
    }
  };

  const updateToken = async () => {
    if (!tokenInfo.value.refreshToken) {
      return;
    }
    try {
      const { data } = await postApiV1RefreshToken(tokenInfo.value);
      tokenInfo.value.accessToken =
        typeof data === 'string'
          ? data
          : ((data as { accessToken?: string } | null | undefined)?.accessToken ?? '');
      accessTokenExpiredTime.value = getAccessTokenExpiredTime();

      setStorage();
    } catch (e) {
      initAuth();
      console.log(e);
    }
  };

  const clearCheckTime = () => {
    if (expiredTimeInfo.value.timer) {
      clearTimeout(expiredTimeInfo.value.timer);
    }
  };

  const checkTimeForRefreshToken = async () => {
    if (!accessTokenExpiredTime.value || getTzTimeNow().diff(accessTokenExpiredTime.value) > 0) {
      expiredTimeInfo.value.isPending = true;
      await updateToken();
      expiredTimeInfo.value.isPending = false;
    }
    expiredTimeInfo.value.timer = setTimeout(checkTimeForRefreshToken, TOKEN_CHECK_CYCLE_SEC);
  };

  const getToken = (type: TokenType | OptionalTokenType) => tokenInfo.value[type];

  const setTokenInfoBySessionStorage = () => {
    const storedVal = webStorageController.getItem({
      type: 'session',
      key: STORAGE_KEY.TOKEN,
    });
    const tokenObj: (Token & { expiredTime?: number }) | null = storedVal
      ? encrypt.decode(storedVal, true)
      : null;

    if (tokenObj) {
      const { accessToken, refreshToken, expiredTime, sessionToken } = tokenObj;
      tokenInfo.value = {
        ...tokenInfo.value,
        accessToken,
        refreshToken,
        sessionToken,
      };

      if (expiredTime) {
        accessTokenExpiredTime.value = expiredTime;
      }
    }
  };

  const setTokenInfoByCookies = async () => {
    const storedVal = getTokenInCookies();
    const sessionTokenObj: TokenInfoInCookies | null = storedVal
      ? encrypt.decode(storedVal, true)
      : null;

    if (sessionTokenObj) {
      const { sessionToken: sessionTokenInCookies } = sessionTokenObj;

      try {
        const response = await postApiV7KeepLogged({
          sessionToken: sessionTokenInCookies,
        });
        const { accessToken = '', refreshToken = '', sessionToken } = response.data ?? {};

        tokenInfo.value = {
          ...tokenInfo.value,
          accessToken,
          refreshToken,
          sessionToken,
        };
        setAccessTokenExpiredTime();
      } catch (e) {
        initAuth();
        console.log(e);
      }
    }
  };

  const setTokenInfoByLinkToken = async () => {
    if (!linkToken.value) {
      return;
    }

    try {
      const { data } = await postApiV8TokenExchange({
        integrationToken: linkToken.value,
      });

      if (!data.accessToken || !data.refreshToken || !data.sessionToken) {
        initAuth();
        resetLinkManagerData();
        return;
      }

      setTokenInfo({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        sessionToken: data.sessionToken,
      });
      setAccessTokenExpiredTime();

      setTokenInHeader(data.accessToken);
      setStorage();

      /**
       * keepLoggedIn: true
       * 쿠키에 sessionToken 정보를 저장함
       */
      if (
        propertyData.value?.keepLoggedIn &&
        !hasTokenInCookies() &&
        tokenInfo.value.sessionToken
      ) {
        setTokenInCookies(tokenInfo.value.sessionToken);
      }

      // 토큰 업데이트 후 linkToken 초기화
      resetLinkToken();
    } catch (e) {
      initAuth();
      resetLinkManagerData();
      console.log(e);
    }
  };

  const setInitialInfo = async () => {
    clearCheckTime();

    if (tokenInfo.value.accessToken) {
      await checkTimeForRefreshToken();
      return;
    }

    setTokenInfoBySessionStorage();

    if (propertyData.value?.keepLoggedIn) {
      /**
       * keepLoggedIn: true
       * 최초 로그인 이후 쿠키에 저장된 토큰을 사용해서 tokenInfo를 설정
       */
      if (!tokenInfo.value.accessToken) {
        await setTokenInfoByCookies();
      }

      /**
       * keepLoggedIn: true
       * 최초 로그인 이후 쿠키에 저장된 토큰이 없으면 초기화 (다른 브라우저에서 로그아웃 한 경우)
       */
      if (!getTokenInCookies()) {
        initAuth();
      }
    }

    if (linkToken.value) {
      await setTokenInfoByLinkToken();
      return;
    }

    if (tokenInfo.value.accessToken) {
      setTokenInHeader(tokenInfo.value.accessToken);
      await checkTimeForRefreshToken();
    }
  };

  useEventListener(document, 'visibilitychange', async () => {
    const { visibilityState } = document;
    const currentPath = router.currentRoute.value?.path;
    if (visibilityState === 'visible' && currentPath !== '/') {
      if (!checkTokenInCookies(!!propertyData.value?.keepLoggedIn, !!tokenInfo.value.accessToken)) {
        router.push('/logout');
      }
      isVisible.value = true;
      clearCheckTime();
      await checkTimeForRefreshToken();
    } else if (visibilityState === 'hidden') {
      isVisible.value = false;
      clearCheckTime();
    }
  });

  return {
    isCallable,
    tokenInfo,
    loginResult,
    passwordInfo,
    clear,
    setTokenLogout,
    login,
    getToken,
    updateToken,
    setInitialInfo,
    checkTimeForRefreshToken,
    ssoLogin,
    shinhanLogin,
    otpLogin,
    fidoLogin,
    requestTwoFactorCode,
    registerNewGoogleOtp,
    completeTwoFactor,
    isValidPasswordByStorage,
    showPasswordChangeConfirmation,
    updateLoginInfo,
  };
});
